/**
 * Local Experience List Component
 *
 * Single Responsibility: Manages and displays a list of local experience items
 * Follows SOLID principles:
 * - Single Responsibility: Only responsible for rendering a list of local experiences
 * - Open/Closed: Open for extension via props, closed for modification
 * - Dependency Inversion: Depends on LocalExperienceItem component interface, not concrete implementation
 */

import React from 'react';
import { spacing } from '@/design-system/utils';
import { LocalExperienceItem } from './LocalExperienceItem';
import { LocalExperience } from './types';

export interface LocalExperienceListProps {
  /**
   * Array of local experience items to display
   */
  items: LocalExperience[];
  /**
   * Place name for image fetching context
   */
  placeName?: string;
  /**
   * Whether to use mobile layout
   */
  isMobile?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Custom gap spacing between items
   */
  gap?: number;
}

/**
 * Local Experience List Component
 * Renders a list of local experience items with consistent spacing
 */
export const LocalExperienceList: React.FC<LocalExperienceListProps> = ({
  items,
  placeName,
  isMobile = false,
  className,
  gap,
}) => {
  if (!items || items.length === 0) {
    return null;
  }

  // Use provided gap or default based on mobile/desktop
  const listGap = gap !== undefined ? gap : isMobile ? spacing[3] : spacing[4];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: listGap,
        padding: `0 ${spacing[3]}px ${spacing[3]}px`,
      }}
      className={className}
    >
      {items.map(experience => (
        <LocalExperienceItem
          key={experience.id}
          experience={experience}
          placeName={placeName}
          isMobile={isMobile}
        />
      ))}
    </div>
  );
};
